import { useEffect, useRef, lazy, Suspense, type ComponentType } from "react";
import { Redirect } from "wouter";
import { useAuth, type Permission } from "@/lib/auth-context";
import { usePermissions } from "@/hooks/use-permission";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { getRoleDashboard } from "@shared/permissions";
import { useToast } from "@/hooks/use-toast";
import type { FeatureKey } from "@shared/features";
import { Spinner } from "@/design-system/components/Spinner";
import { LoadingState } from "@/design-system/components/LoadingState";

const UpgradeRequired = lazy(() =>
  import("@/components/shared/UpgradeRequired").then((m) => ({ default: m.UpgradeRequired })),
);

/**
 * Canonical route gate (Task #1730, updated Task #2051).
 *
 * Single implementation that absorbs the previous six route-guard wrappers
 * (`ProtectedRoute`, `FeatureGuardedRoute`, `SuperAdminRoute`, `WorkerRoute`,
 * `WorkerFeatureRoute`, `PartnerRoute`). The legacy wrapper names remain
 * exported below as 1-line aliases so the IA validators
 * (`scripts/validate-workforce-ia.ts`) keep recognising them by name; their
 * implementations all delegate here.
 *
 * Behaviour matches the previous wrappers exactly:
 *   - Defers render while auth/flags are still resolving (returns null).
 *   - For `feature` props (single key or array), every key must be enabled
 *     via the canonical `useFeatureFlags().isEnabled(...)`. The hook is
 *     fail-closed — unknown / disabled keys yield `<UpgradeRequired />`.
 *   - For `requireSuperAdmin`, asserts the `super_admin` permission.
 *   - For `requireWorker`, asserts the worker role OR a `linkedWorkerId`.
 *   - For `permission` (single or array — OR semantics on arrays), asserts
 *     the active session holds at least one matching permission, redirects
 *     to `/auth/unauthorized` otherwise.
 *
 * Task #2051 addition — partner capability gating is now expressible directly
 * on `RouteGate` via `requirePartnerCapability` and `allowUnassigned`, so
 * `PartnerRoute` becomes a thin alias (see below). The toast side-effect for
 * capability denial stays in PartnerRoute's useEffect so it doesn't fire
 * during the render pass.
 */
export type RouteGateProps = {
  component: ComponentType<any>;
  componentProps?: Record<string, unknown>;
  feature?: FeatureKey | FeatureKey[];
  permission?: Permission | Permission[];
  requireSuperAdmin?: boolean;
  requireWorker?: boolean;
  /** When true, any user with a linked worker record (`currentUser.linkedWorkerId`)
   *  is allowed through the permission check unconditionally. Use on routes where
   *  `linkedWorkerId` is the primary entitlement signal (e.g. My Workspace hub). */
  linkedWorkerOk?: boolean;
  /** One or more partner capability keys (OR semantics). When set, the user
   *  must be a partner-company user holding at least one of these capabilities
   *  or they are redirected to /partner/home. */
  requirePartnerCapability?: string | string[];
  /** When true, unassigned partner users are allowed through rather than
   *  being redirected to /partner/home. Used on partner onboarding screens. */
  allowUnassigned?: boolean;
  /**
   * When set, a disabled feature redirects to this path instead of showing
   * the <UpgradeRequired /> page. Useful for route-level feature gating where
   * a redirect is more appropriate than an upgrade prompt (e.g. platform.multiSite
   * off → redirect to /sites instead of showing "upgrade required").
   */
  redirectTo?: string;
};

export function RouteGate({
  component: Component,
  componentProps,
  feature,
  permission,
  requireSuperAdmin,
  requireWorker,
  linkedWorkerOk,
  requirePartnerCapability,
  allowUnassigned,
  redirectTo,
}: RouteGateProps) {
  const { isLoading, isVerifyingSession, hasRole, currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const partnerCtx = usePartnerContext();

  const partnerNeeded = requirePartnerCapability !== undefined || allowUnassigned !== undefined;
  const flagsNeeded = feature !== undefined;
  // PTW-016: on a warm start the shell renders with isLoading=false before the
  // background /api/auth/me check resolves. Evaluating the permission gate now
  // (with currentUser still null) would wrongly redirect an authorized user to
  // /auth/unauthorized. Wait for the session to be confirmed first.
  if (isLoading || isVerifyingSession || (flagsNeeded && flagsLoading) || (partnerNeeded && partnerCtx.isLoading))
    return <LoadingState className="min-h-[50vh]" />;

  if (feature !== undefined) {
    const features = Array.isArray(feature) ? feature : [feature];
    for (const f of features) {
      if (!isEnabled(f)) {
        if (redirectTo) {
          return <Redirect to={redirectTo} />;
        }
        return (
          <Suspense fallback={null}>
            <UpgradeRequired feature={f} />
          </Suspense>
        );
      }
    }
  }

  if (requireSuperAdmin && !hasPermission("super_admin")) {
    return <Redirect to="/auth/unauthorized" />;
  }

  if (requireWorker && !hasRole("worker") && !currentUser?.linkedWorkerId) {
    return <Redirect to="/auth/unauthorized" />;
  }

  if (permission !== undefined) {
    const linkedWorkerEntitled = linkedWorkerOk && !!currentUser?.linkedWorkerId;
    if (!linkedWorkerEntitled) {
      const perms = Array.isArray(permission) ? permission : [permission];
      if (!perms.some((p) => hasPermission(p))) return <Redirect to="/auth/unauthorized" />;
    }
  }

  // Partner capability gating (Task #2051 — moved from PartnerRoute bespoke logic).
  // Note: toast side-effect for capability denial is NOT fired here to avoid
  // side effects during render; callers that want the toast use PartnerRoute below.
  if (partnerNeeded) {
    const isPartnerUser = partnerCtx.isPartnerCompanyUser;
    const isUnassigned = partnerCtx.isUnassigned;

    if (!allowUnassigned && isUnassigned) return <Redirect to="/partner/home" />;

    if (requirePartnerCapability !== undefined && isPartnerUser && !isUnassigned) {
      const caps = Array.isArray(requirePartnerCapability) ? requirePartnerCapability : [requirePartnerCapability];
      const denied = !caps.some((c) => partnerCtx.effectiveCapabilities[c]);
      if (denied) return <Redirect to="/partner/home" />;
    }
  }

  return <Component {...(componentProps ?? {})} />;
}

// ─── Legacy named wrappers (kept for IA-validator name detection) ───────────
// All of these now delegate to <RouteGate>. The IA validator
// (`scripts/validate-workforce-ia.ts`) recognises gate wrappers by name, so
// we keep the public surface unchanged while collapsing the implementation
// into a single canonical gate.

export function ProtectedRoute(props: {
  permission: Permission | Permission[];
  component: ComponentType<any>;
  componentProps?: Record<string, unknown>;
  linkedWorkerOk?: boolean;
}) {
  return <RouteGate {...props} />;
}

export function SuperAdminRoute({ component }: { component: ComponentType }) {
  return <RouteGate component={component} requireSuperAdmin />;
}

export function WorkerRoute({ component, permission }: { component: ComponentType; permission?: Permission }) {
  return <RouteGate component={component} requireWorker permission={permission} />;
}

export function FeatureGuardedRoute(props: {
  feature: FeatureKey | FeatureKey[];
  permission: Permission | Permission[];
  component: ComponentType<any>;
  componentProps?: Record<string, unknown>;
  redirectTo?: string;
}) {
  return <RouteGate {...props} />;
}

export function WorkerFeatureRoute({
  feature,
  component,
  permission,
}: {
  feature: FeatureKey;
  component: ComponentType;
  permission?: Permission;
}) {
  return <RouteGate component={component} requireWorker feature={feature} permission={permission} />;
}

/**
 * Partner-specific route guard (Task #2051 — simplified to a thin alias).
 *
 * The base authorization check (capability gating, unassigned redirect) is
 * now expressed via `RouteGate`'s `requirePartnerCapability` / `allowUnassigned`
 * props. This component adds the partner-context loader guard and the toast
 * side-effect for capability denial on top of the canonical gate.
 *
 * The outer shell remains a named function so the IA validator
 * (`scripts/validate-workforce-ia.ts`) keeps recognising it by name.
 */
export function PartnerRoute({
  capability,
  permission,
  allowUnassigned,
  component: Component,
}: {
  capability?: string | string[];
  permission?: Permission | Permission[];
  allowUnassigned?: boolean;
  component: ComponentType;
}) {
  const { currentUser, isLoading, isVerifyingSession } = useAuth();
  const { hasPermission } = usePermissions();
  const partnerCtx = usePartnerContext();
  const { toast } = useToast();
  const capDeniedRef = useRef(false);

  const isPartnerUser = !partnerCtx.isLoading && partnerCtx.isPartnerCompanyUser;

  const shouldDenyCapability =
    !!(!partnerCtx.isLoading && !partnerCtx.isUnassigned && isPartnerUser && capability) &&
    (() => {
      const caps = Array.isArray(capability) ? capability : [capability!];
      return !caps.some((c) => partnerCtx.effectiveCapabilities[c]);
    })();

  useEffect(() => {
    if (shouldDenyCapability && !capDeniedRef.current) {
      capDeniedRef.current = true;
      toast({
        variant: "destructive",
        title: "Access restricted",
        description: "Your company does not have permission to access this section.",
      });
    }
  }, [shouldDenyCapability, toast]);

  if (isLoading || isVerifyingSession || partnerCtx.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!currentUser) return <Redirect to="/auth/login" />;
  if (hasPermission("super_admin")) return <Redirect to="/super-admin/dashboard" />;
  if (!partnerCtx.isLoading && !isPartnerUser) {
    const targetDashboard = getRoleDashboard(currentUser?.role ?? "");
    return <Redirect to={targetDashboard || "/dashboard"} />;
  }

  // Delegate remaining checks (unassigned redirect + capability redirect) to RouteGate
  return (
    <RouteGate
      component={Component}
      permission={permission}
      requirePartnerCapability={capability}
      allowUnassigned={allowUnassigned}
    />
  );
}
